import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import api from '../utils/api';
import { useTranslation } from 'react-i18next';

/**
 * Accounting-grade financial reports — ONE component, reused by the
 * TreatX Accounting dashboard, the Super Admin finance page and the Provider
 * accountant workspace. Role scoping is enforced server-side: the same
 * endpoints return the platform view for platform roles and the caller's own
 * clinic for a provider.
 *
 * Every figure is read from PostgreSQL (the financial authority) and is
 * currency-separated. Nothing is aggregated across currencies.
 */

const REPORTS = [
  { key: 'trial-balance', label: 'Trial Balance' },
  { key: 'general-ledger', label: 'General Ledger' },
  { key: 'ar-ap', label: 'AR / AP + Ageing' },
  { key: 'deferred-revenue', label: 'Deferred Revenue' },
  { key: 'vat-summary', label: 'VAT / Tax' },
  { key: 'psp-settlement', label: 'PSP Settlement' },
  { key: 'subscription-financials', label: 'Subscriptions' },
];

// Statuses that are an EXPLICIT success. Everything else — including a missing
// check, `not_evaluated`, `indeterminate` or any status this build does not
// recognise — is NOT success and must never be rendered as reconciled.
const RECONCILED_STATUSES = Object.freeze(['reconciled', 'matched']);
const STATUS_SEVERITY = Object.freeze({
  reconciled: 0, matched: 0,
  discrepant: 3, variance: 3,
  indeterminate: 2, currency_differs: 2, absent: 2, not_evaluated: 2,
  unavailable: 2, unknown: 2,
});

// TREATX-FIN-ENV-PROVENANCE-01 — Sandbox activity is TEST financial activity
// and must never sit inside an operational total. The scope is sent to the
// server, which filters on the canonical `journal_entries.psp_environment`.
const ENVIRONMENTS = ['operational', 'live', 'sandbox', 'all'];

const AGEING_ORDER = [
  ['future', 'Future'],
  ['due_now', 'Due today'],
  ['overdue_1_30', '1–30 days'],
  ['overdue_31_60', '31–60 days'],
  ['overdue_61_90', '61–90 days'],
  ['overdue_90_plus', '90+ days'],
  ['no_due_date', 'No due date'],
];

const money = (v, ccy) =>
  `${ccy} ${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CurrencyNote = () => (
  <p className="text-[11px] text-muted-foreground mt-2" data-testid="currency-separation-note">
    Balances are reported per currency. No cross-currency total is produced without an authorised FX conversion.
  </p>
);

const Empty = ({ what }) => {
  const { t } = useTranslation();
  return (
    <p className="text-xs text-muted-foreground py-4" data-testid="report-empty">
      {t('accountingReports.empty', { what })}
    </p>
  );
};

function TrialBalance({ data }) {
  const groups = data.by_currency || [];
  if (!groups.length) return <Empty what="posted journal lines" />;
  return (
    <div className="space-y-5" data-testid="trial-balance-report">
      {groups.map(g => (
        <div key={g.currency} data-testid={`trial-balance-${g.currency}`}>
          <div className="flex items-center gap-2 mb-2">
            <h4 className="text-sm font-semibold">{g.currency}</h4>
            <Badge variant="outline"
              className={`text-[10px] ${g.balanced ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}
              data-testid={`trial-balance-balanced-${g.currency}`}>
              {g.balanced ? 'Balanced' : `Out of balance by ${g.difference}`}
            </Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-1.5 pr-3">Account</th>
                  <th className="text-left py-1.5 pr-3">Classification</th>
                  <th className="text-right py-1.5 pr-3">Debit</th>
                  <th className="text-right py-1.5 pr-3">Credit</th>
                  <th className="text-right py-1.5">Balance</th>
                </tr>
              </thead>
              <tbody>
                {(g.accounts || []).map(a => (
                  <tr key={`${a.account_code}-${g.currency}`} className="border-b last:border-0">
                    <td className="py-1.5 pr-3 font-mono">{a.account_code} · {a.account_name}</td>
                    <td className="py-1.5 pr-3 text-muted-foreground">{a.movement_type}</td>
                    <td className="py-1.5 pr-3 text-right">{money(a.debit, g.currency)}</td>
                    <td className="py-1.5 pr-3 text-right">{money(a.credit, g.currency)}</td>
                    <td className="py-1.5 text-right font-medium">
                      {money(a.balance, g.currency)} <span className="text-muted-foreground">{a.balance_direction}</span>
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td className="py-1.5 pr-3" colSpan={2}>Total {g.currency}</td>
                  <td className="py-1.5 pr-3 text-right">{money(g.total_debit, g.currency)}</td>
                  <td className="py-1.5 pr-3 text-right">{money(g.total_credit, g.currency)}</td>
                  <td className="py-1.5 text-right">{money(g.difference, g.currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <CurrencyNote />
    </div>
  );
}

function GeneralLedger({ data }) {
  const accounts = data.accounts || [];
  if (!accounts.length) return <Empty what="posted journal lines" />;
  return (
    <div className="space-y-5" data-testid="general-ledger-report">
      {accounts.map(acc => (
        <div key={`${acc.account_code}-${acc.currency}`}
          data-testid={`gl-account-${acc.account_code}-${acc.currency}`}>
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <h4 className="text-sm font-semibold font-mono">{acc.account_code} · {acc.account_name}</h4>
            <Badge variant="outline" className="text-[10px]">{acc.currency}</Badge>
            <Badge variant="outline" className="text-[10px]">{acc.movement_type}</Badge>
            <span className="text-[11px] text-muted-foreground">
              Legal owner: {acc.legal_owner === 'treatx' ? 'TreatX' : acc.legal_owner}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-1.5 pr-3">Date</th>
                  <th className="text-left py-1.5 pr-3">Description</th>
                  <th className="text-left py-1.5 pr-3">Counterparty</th>
                  <th className="text-right py-1.5 pr-3">Debit</th>
                  <th className="text-right py-1.5 pr-3">Credit</th>
                  <th className="text-right py-1.5">Running balance</th>
                </tr>
              </thead>
              <tbody>
                {(acc.lines || []).map(l => (
                  <tr key={`${l.journal_id}-${l.line_number}`} className="border-b last:border-0">
                    <td className="py-1.5 pr-3 whitespace-nowrap">{l.entry_date}</td>
                    <td className="py-1.5 pr-3">{l.description || l.memo}</td>
                    <td className="py-1.5 pr-3">
                      {l.counterparty_clinic_id || <span className="text-muted-foreground">unattributed</span>}
                    </td>
                    <td className="py-1.5 pr-3 text-right">{l.debit ? money(l.debit, acc.currency) : '—'}</td>
                    <td className="py-1.5 pr-3 text-right">{l.credit ? money(l.credit, acc.currency) : '—'}</td>
                    <td className="py-1.5 text-right font-medium">{money(l.running_balance, acc.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Closing balance {money(acc.closing_balance, acc.currency)} ({acc.balance_direction})
          </p>
        </div>
      ))}
      {data.truncated && (
        <p className="text-[11px] text-amber-700" data-testid="gl-truncated">
          Result truncated — narrow the date range or filter by account.
        </p>
      )}
      <CurrencyNote />
    </div>
  );
}

function AgeingTable({ bucketData, currency }) {
  return (
    // Scrolls inside its own box: without this the table pushed the whole page
    // sideways on a phone, hiding the first column.
    <div className="overflow-x-auto">
    <table className="w-full text-xs">
      <thead className="text-muted-foreground border-b">
        <tr>
          <th className="text-left py-1.5 pr-3">Ageing bucket</th>
          <th className="text-right py-1.5 pr-3">Items</th>
          <th className="text-right py-1.5">Open amount</th>
        </tr>
      </thead>
      <tbody>
        {AGEING_ORDER.map(([key, label]) => (
          <tr key={key} className="border-b last:border-0">
            <td className="py-1.5 pr-3">{label}</td>
            <td className="py-1.5 pr-3 text-right">{bucketData?.[key]?.count || 0}</td>
            <td className="py-1.5 text-right">{money(bucketData?.[key]?.amount, currency)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function ArAp({ data }) {
  const { t } = useTranslation();
  const ar = data.receivables_subscription || [];
  const comm = data.receivables_commission || [];
  const ap = data.payables_provider || [];
  const apUnclassified = data.payables_unclassified || [];
  const unclassifiedSources = data.unclassified_sources || [];
  const excluded = Number(data.payables_excluded_by_environment || 0);
  const scope = data.environment_scope;
  return (
    <div className="space-y-6" data-testid="ar-ap-report">
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-[11px] text-blue-900"
        data-testid="ar-ap-basis-note">
        Posted obligations only. Contractual future cash-flow forecasts are not accounting balances and are excluded.
      </div>

      {/* ACCOUNTING-TRUTH-01 (B5) — the backend states, per source, whether the
          selected Sandbox/Live scope could actually be applied. A backend note
          that nothing renders is not a disclosure, so the scope the reader is
          actually looking at is shown here, in words. */}
      <div className="bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-[11px] text-slate-800"
        data-testid="ar-ap-environment-note">
        <span className="font-medium">
          {t('accountingReports.arap.scopeLabel', { scope })}
        </span>{' '}
        {data.environment_note}
        {unclassifiedSources.length > 0 && (
          <div className="mt-1" data-testid="ar-ap-unclassified-sources">
            {t('accountingReports.arap.unclassifiedSources', {
              sources: unclassifiedSources.join(', '),
            })}
          </div>
        )}
        {excluded > 0 && (
          <div className="mt-1" data-testid="ar-ap-environment-excluded">
            {t('accountingReports.arap.excluded', { count: excluded })}
          </div>
        )}
      </div>

      <section>
        <h4 className="text-sm font-semibold mb-2">Subscription receivables</h4>
        {!ar.length ? <Empty what="open subscription receivables" /> : ar.map(g => (
          <div key={g.currency} className="mb-4" data-testid={`ar-subscription-${g.currency}`}>
            <div className="flex items-center gap-3 mb-1.5 text-xs flex-wrap">
              <Badge variant="outline" className="text-[10px]">{g.currency}</Badge>
              <span>Open {money(g.open_total, g.currency)}</span>
              <span className="text-red-600">Overdue {money(g.overdue_total, g.currency)}</span>
              <span className="text-muted-foreground">Future {money(g.future_total, g.currency)}</span>
            </div>
            <AgeingTable bucketData={g.ageing} currency={g.currency} />
          </div>
        ))}
      </section>

      <section>
        <h4 className="text-sm font-semibold mb-2">Commission receivables</h4>
        {!comm.length ? <Empty what="commission receivables" /> : comm.map(g => (
          <div key={g.currency} className="mb-4" data-testid={`ar-commission-${g.currency}`}>
            <div className="flex items-center gap-3 mb-1.5 text-xs">
              <Badge variant="outline" className="text-[10px]">{g.currency}</Badge>
              <span>{g.count} invoice(s) · {money(g.open_total, g.currency)}</span>
            </div>
            <AgeingTable bucketData={g.ageing} currency={g.currency} />
          </div>
        ))}
      </section>

      {/* ACCOUNTING-TRUTH-01 (B6) — named for what it actually reads: the
          withdrawal-request queue. It is not the PSP-reported provider share,
          not a recognised payable and not a release-eligible amount. */}
      <section>
        <h4 className="text-sm font-semibold mb-2" data-testid="ap-provider-title">
          {data.payables_title || t('accountingReports.arap.payablesTitle')}
        </h4>
        {!ap.length ? (
          <Empty what={t('accountingReports.arap.payablesEmpty')} />
        ) : ap.map(g => (
          <div key={g.currency} className="mb-2 text-xs" data-testid={`ap-provider-${g.currency}`}>
            <Badge variant="outline" className="text-[10px] mr-2">{g.currency}</Badge>
            Open {money(g.open_total, g.currency)}
            {Object.entries(g.by_status || {}).map(([st, v]) => (
              <span key={st} className="ml-3 text-muted-foreground">
                {st}: {v.count} · {money(v.amount, g.currency)}
              </span>
            ))}
          </div>
        ))}
        <p className="text-[11px] text-muted-foreground mt-1" data-testid="ap-provider-scope-note">
          {data.payables_note}
        </p>
      </section>

      {apUnclassified.length > 0 && (
        <section data-testid="ap-unclassified">
          <h4 className="text-sm font-semibold mb-2">
            {t('accountingReports.arap.unclassifiedTitle')}
          </h4>
          {apUnclassified.map(g => (
            <div key={g.currency} className="mb-2 text-xs"
              data-testid={`ap-unclassified-${g.currency}`}>
              <Badge variant="outline" className="text-[10px] mr-2">{g.currency}</Badge>
              {money(g.open_total, g.currency)}
            </div>
          ))}
          <p className="text-[11px] text-amber-700 mt-1">
            {t('accountingReports.arap.unclassifiedNote', { scope })}
          </p>
        </section>
      )}
      <CurrencyNote />
    </div>
  );
}

function DeferredRevenue({ data }) {
  const sched = data.schedule_by_currency || [];
  const positions = data.ledger_positions_by_currency || {};
  if (!sched.length && !Object.keys(positions).length) {
    return <Empty what="deferred revenue" />;
  }
  return (
    <div className="space-y-5" data-testid="deferred-revenue-report">
      {sched.map(g => (
        <div key={g.currency} data-testid={`deferred-${g.currency}`}>
          <div className="flex items-center gap-3 mb-1.5 text-xs flex-wrap">
            <Badge variant="outline" className="text-[10px]">{g.currency}</Badge>
            <span>Deferred {money(g.deferred_total, g.currency)}</span>
            <span className="text-emerald-700">Recognised {money(g.recognised_total, g.currency)}</span>
            <span className="text-blue-700">Remaining {money(g.remaining_total, g.currency)}</span>
            <span className="text-muted-foreground">{g.entries} schedule entries</span>
            {g.on_hold_entries > 0 && (
              <span className="text-amber-700">{g.on_hold_entries} on hold</span>
            )}
          </div>
          {(positions[g.currency] || []).map(p => (
            <div key={p.account_code} className="text-xs text-muted-foreground font-mono">
              Ledger {p.account_code} {p.account_name}: {money(p.balance, g.currency)} {p.balance_direction}
            </div>
          ))}
        </div>
      ))}
      <CurrencyNote />
    </div>
  );
}

function VatSummary({ data }) {
  const acct = data.output_vat_account || {};
  const groups = data.invoices_by_currency || [];
  return (
    <div className="space-y-5" data-testid="vat-summary-report">
      <div className="text-[11px] text-muted-foreground" data-testid="vat-policy">{data.policy}</div>
      {acct.status !== 'approved' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-[11px] text-amber-900 flex gap-2"
          data-testid="vat-account-pending">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <span>
            Output-VAT liability account is <strong>pending approval</strong> (blocker {acct.blocker}). {acct.effect}
          </span>
        </div>
      )}
      {!groups.length ? <Empty what="subscription invoices" /> : groups.map(g => (
        <div key={g.currency} data-testid={`vat-${g.currency}`}>
          <div className="flex items-center gap-3 mb-1.5 text-xs flex-wrap">
            <Badge variant="outline" className="text-[10px]">{g.currency}</Badge>
            <span>Net {money(g.net_total, g.currency)}</span>
            <span>VAT {money(g.vat_total, g.currency)}</span>
            <span className="font-medium">Gross {money(g.gross_total, g.currency)}</span>
            <Badge variant="outline"
              className={`text-[10px] ${g.net_plus_vat_equals_gross ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}
              data-testid={`vat-reconciled-${g.currency}`}>
              {g.net_plus_vat_equals_gross ? 'Net + VAT = Gross' : 'Reconciliation failed'}
            </Badge>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-b">
              <tr>
                <th className="text-left py-1.5 pr-3">Tax treatment</th>
                <th className="text-right py-1.5 pr-3">Rate (bps)</th>
                <th className="text-right py-1.5 pr-3">Invoices</th>
                <th className="text-right py-1.5 pr-3">Net</th>
                <th className="text-right py-1.5 pr-3">VAT</th>
                <th className="text-right py-1.5">Gross</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(g.by_treatment || {}).map(([t, v]) => (
                <tr key={t} className="border-b last:border-0">
                  <td className="py-1.5 pr-3">{t}</td>
                  <td className="py-1.5 pr-3 text-right">{v.tax_rate_bps ?? '—'}</td>
                  <td className="py-1.5 pr-3 text-right">{v.invoice_count}</td>
                  <td className="py-1.5 pr-3 text-right">{money(v.net, g.currency)}</td>
                  <td className="py-1.5 pr-3 text-right">{money(v.vat, g.currency)}</td>
                  <td className="py-1.5 text-right">{money(v.gross, g.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      ))}
      <p className="text-[11px] text-muted-foreground" data-testid="vat-treatments-note">
        Not implemented: {(data.treatments_not_implemented || []).join(', ')}. {data.treatments_note}
      </p>
    </div>
  );
}

export function distributionVerdict(row) {
  // The overall distribution verdict is the WORST of its parts, never one of
  // them alone. Reading only `distribution_vs_collection` showed "reconciled"
  // for a settlement gross of 100 split as provider 90 + TreatX 40: the gross
  // did tie back to the collection, while the COMPONENTS were 30 out.
  //
  // It then went on to FILTER OUT the checks that were missing and to treat any
  // status it did not recognise (`not_evaluated`, `absent`, a future status) as
  // a pass, because those fell through the rank table with rank 0. A reconciled
  // verdict now requires EVERY required check to be present and explicitly
  // successful; absent, unknown and unevaluated are never success.
  const required = [row.payout_reconciliation, row.distribution_vs_collection];
  if (required.some((p) => !p || !p.status)) {
    return { status: 'unavailable', reason: 'required_check_missing' };
  }
  if (required.every((p) => RECONCILED_STATUSES.includes(p.status))) {
    return { status: 'reconciled' };
  }
  const severity = (p) => (STATUS_SEVERITY[p.status] === undefined
    ? 2 : STATUS_SEVERITY[p.status]);
  return required.reduce((a, b) => (severity(b) > severity(a) ? b : a));
}

function CollectionOperations({ rows, unattributable }) {
  // PSP-3 — per-collection traceability. The backend has returned this for a
  // while and NOTHING displayed it, so a discrepant distribution was invisible
  // in Accounting. Currencies are shown per row and deliberately NEVER totalled
  // across rows: a collection currency and a settlement currency are different
  // facts about different money.
  const { t } = useTranslation();
  const verdict = (v) => {
    if (!v || !v.status) {
      return (
        <span className="text-muted-foreground">
          {t('accountingReports.collections.status.unavailable')}
        </span>
      );
    }
    const tone = ({
      matched: 'text-emerald-700', reconciled: 'text-emerald-700',
      variance: 'text-red-700', discrepant: 'text-red-700',
      indeterminate: 'text-amber-700', currency_differs: 'text-amber-700',
      not_evaluated: 'text-amber-700', unavailable: 'text-amber-700',
      absent: 'text-muted-foreground',
    })[v.status] || 'text-amber-700';
    const diff = v.difference_minor_units ?? v.variance_minor_units;
    return (
      <span className={tone}>
        {t(`accountingReports.collections.status.${v.status}`, {
          defaultValue: v.status,
        })}
        {diff ? ` (${diff > 0 ? '+' : ''}${diff})` : ''}
      </span>
    );
  };
  // ACCOUNTING-TRUTH-01 (C3) — the residual is stated with its UNIT and
  // CURRENCY and with the reason it exists, not hidden in a tooltip. The
  // owner's screenshot showed TRY 116,265.30 gross against components totalling
  // TRY 116,265.31: a one-minor-unit display artefact, because each component
  // is rounded to minor units on its own. The exact disclosure is what
  // balances; no PSP amount is altered and no discrepancy is waved through as
  // "rounding" without that evidence.
  const rounding = (r) => {
    const d = r.display_rounding || {};
    const residual = r.rounding_residual_minor_units || {};
    const diff = d.difference_minor_units;
    const ccy = r.settlement_currency || r.currency || '';
    const entries = Object.entries(residual);
    if (!diff && !entries.length) {
      return <span className="text-muted-foreground">—</span>;
    }
    const signed = diff
      ? `${diff > 0 ? '+' : '−'}${(Math.abs(diff) / 100).toFixed(2)} ${ccy}`
      : null;
    return (
      <span className="text-amber-700 whitespace-nowrap"
        data-testid="psp-rounding-detail">
        {signed
          ? t('accountingReports.collections.rounding.displayUnit', {
            amount: signed, minor: diff > 0 ? `+${diff}` : String(diff),
          })
          : t('accountingReports.collections.rounding.residual')}
        {entries.length > 0 && (
          <span className="block text-[10px] text-muted-foreground">
            {entries
              .map(([k, v]) => `${k} ${v > 0 ? '+' : ''}${v}`)
              .join(' · ')} {t('accountingReports.collections.rounding.unitSuffix', { ccy })}
          </span>
        )}
      </span>
    );
  };
  const crossCurrency = (r) => {
    // A EUR collection settling in TRY can only be LINKED to its distribution,
    // never compared with it. The relationship, and the PSP identity that
    // establishes it, are stated here — so a converted settlement is neither
    // read as a discrepancy nor presented as a full reconciliation.
    const link = r.cross_currency_link;
    if (!link || !link.relationship || link.relationship === 'same_currency') {
      return <span className="text-muted-foreground">—</span>;
    }
    const ids = (link.linked_by || {});
    return (
      <span className={link.relationship === 'linked_by_psp_identity'
        ? 'text-sky-700' : 'text-amber-700'}
        title={[link.proves, ...(link.does_not_prove || [])].filter(Boolean).join(' · ')}>
        {t(`accountingReports.collections.crossCurrency.${link.relationship}`, {
          defaultValue: link.relationship,
          from: (link.collection || {}).currency || '?',
          to: (link.settlement_distribution || {}).currency || '?',
        })}
        {ids.psp_payment_id ? ` · ${ids.psp_payment_id}` : ''}
      </span>
    );
  };
  const completeness = (r) => {
    const c = r.completeness || 'unknown';
    if (c === 'complete') return null;
    return (
      <span className="text-amber-700 ml-1"
        title={t(`accountingReports.collections.completeness.${c}`, { defaultValue: c })}>
        {t(`accountingReports.collections.completeness.${c}`, { defaultValue: c })}
      </span>
    );
  };
  return (
    <section data-testid="psp-collection-operations">
      <h4 className="text-sm font-semibold mb-2">
        {t('accountingReports.collections.title')}
      </h4>
      {!rows.length ? <Empty what="patient collections" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-b">
              <tr>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.collections.columns.psp')}</th>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.collections.columns.environment')}</th>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.collections.columns.payment')}</th>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.collections.columns.obligation')}</th>
                <th className="text-right py-1.5 pr-3">{t('accountingReports.collections.columns.collected')}</th>
                <th className="text-right py-1.5 pr-3">{t('accountingReports.collections.columns.settlementGross')}</th>
                <th className="text-right py-1.5 pr-3">{t('accountingReports.collections.columns.provider')}</th>
                <th className="text-right py-1.5 pr-3">{t('accountingReports.collections.columns.treatx')}</th>
                <th className="text-right py-1.5 pr-3">{t('accountingReports.collections.columns.pspCharges')}</th>
                <th className="text-right py-1.5 pr-3">{t('accountingReports.collections.columns.blockage')}</th>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.collections.columns.collectionRecon')}</th>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.collections.columns.components')}</th>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.collections.columns.grossVsCollection')}</th>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.collections.columns.distributionVerdict')}</th>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.collections.columns.rounding')}</th>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.collections.columns.crossCurrency')}</th>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.collections.columns.payout')}</th>
                <th className="text-left py-1.5">{t('accountingReports.collections.columns.bankCredit')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const sc = r.settlement_currency;
                const minor = (v, c) => (v === null || v === undefined
                  ? (
                    <span className="text-muted-foreground">
                      {t('accountingReports.collections.status.unavailable')}
                    </span>
                  )
                  : `${(v / 100).toFixed(2)} ${c || ''}`);
                return (
                  <tr key={r.psp_operation_id || i} className="border-b last:border-0"
                      data-testid={`psp-collection-row-${i}`}>
                    <td className="py-1.5 pr-3" data-testid={`psp-collection-psp-${i}`}>{r.psp}</td>
                    <td className="py-1.5 pr-3" data-testid={`psp-collection-env-${i}`}>
                      {r.psp_environment || 'unknown'}
                    </td>
                    <td className="py-1.5 pr-3 font-mono">{(r.payment_id || '—').slice(0, 14)}</td>
                    <td className="py-1.5 pr-3 font-mono">{(r.obligation_id || '—').slice(0, 14)}</td>
                    <td className="py-1.5 pr-3 text-right" data-testid={`psp-collection-collected-${i}`}>
                      {minor(r.collected_minor_units, r.currency)}
                    </td>
                    <td className="py-1.5 pr-3 text-right">
                      {minor(r.collection_reconciliation ? (r.distribution_vs_collection
                        || {}).disclosed_settlement_gross_minor_units : null, sc)}
                    </td>
                    <td className="py-1.5 pr-3 text-right">
                      {minor(r.provider_payout_minor_units, sc)}{completeness(r)}
                    </td>
                    <td className="py-1.5 pr-3 text-right">
                      {minor(r.treatx_payout_minor_units, sc)}
                    </td>
                    <td className="py-1.5 pr-3 text-right">
                      {minor(r.psp_cost_settlement_minor_units, r.psp_cost_settlement_currency)}
                    </td>
                    <td className="py-1.5 pr-3 text-right">{minor(r.blockage_minor_units, sc)}</td>
                    <td className="py-1.5 pr-3" data-testid={`psp-collection-recon-${i}`}>
                      {verdict(r.collection_reconciliation)}
                    </td>
                    <td className="py-1.5 pr-3" data-testid={`psp-components-recon-${i}`}>
                      {verdict(r.payout_reconciliation)}
                    </td>
                    <td className="py-1.5 pr-3" data-testid={`psp-gross-recon-${i}`}>
                      {verdict(r.distribution_vs_collection)}
                    </td>
                    <td className="py-1.5 pr-3" data-testid={`psp-distribution-recon-${i}`}>
                      {verdict(distributionVerdict(r))}
                    </td>
                    <td className="py-1.5 pr-3" data-testid={`psp-rounding-${i}`}>
                      {rounding(r)}
                    </td>
                    <td className="py-1.5 pr-3" data-testid={`psp-cross-currency-${i}`}>
                      {crossCurrency(r)}
                    </td>
                    <td className="py-1.5 pr-3" data-testid={`psp-collection-payout-${i}`}>
                      {r.usable_for_payout
                        ? <span className="text-emerald-700">{t('accountingReports.collections.payout.usable')}</span>
                        : <span className="text-amber-700">{t('accountingReports.collections.payout.notUsable')}</span>}
                    </td>
                    <td className="py-1.5" data-testid={`psp-collection-bank-${i}`}>
                      {r.bank_credit_verified
                        ? <span className="text-emerald-700">{t('accountingReports.collections.bank.confirmed')}</span>
                        : <span className="text-muted-foreground">{t('accountingReports.collections.bank.notConfirmed')}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[11px] text-muted-foreground mt-2" data-testid="psp-collection-note">
            {t('accountingReports.collections.note')}
          </p>
        </div>
      )}
      {unattributable > 0 && (
        <p className="text-[11px] text-amber-700 mt-2" data-testid="psp-unattributable">
          {unattributable} reconciliation record(s) reference no resolvable entity and
          cannot be attributed to any tenant or environment.
        </p>
      )}
    </section>
  );
}

function PspSettlement({ data }) {
  const { t } = useTranslation();
  const ops = data.operations || [];
  const auths = data.settlement_authorizations || [];
  const recon = data.reconciliation || [];
  return (
    <div className="space-y-5" data-testid="psp-settlement-report">
      <section>
        <h4 className="text-sm font-semibold mb-2">{t('accountingReports.operations.title')}</h4>
        {!ops.length ? <Empty what={t('accountingReports.operations.title')} /> : (
          // At 390px this table was cut off at the card edge and the Amount
          // column was invisible. It scrolls inside its own container now, as
          // the collections table already did.
          <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-b">
              <tr>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.operations.columns.provider')}</th>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.operations.columns.type')}</th>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.operations.columns.status')}</th>
                <th className="text-left py-1.5 pr-3">{t('accountingReports.operations.columns.currency')}</th>
                <th className="text-right py-1.5 pr-3">{t('accountingReports.operations.columns.count')}</th>
                <th className="text-right py-1.5">{t('accountingReports.operations.columns.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {ops.map((o, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-1.5 pr-3">{o.psp_provider}</td>
                  <td className="py-1.5 pr-3">{o.operation_type}</td>
                  <td className="py-1.5 pr-3">{o.status}</td>
                  <td className="py-1.5 pr-3">{o.currency}</td>
                  <td className="py-1.5 pr-3 text-right">{o.count}</td>
                  <td className="py-1.5 text-right">{money(o.amount, o.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>
      <CollectionOperations
        rows={data.collection_operations || []}
        unattributable={Number(data.reconciliation_unattributable_records || 0)}
      />
      <section>
        <h4 className="text-sm font-semibold mb-2">Settlement authorisations</h4>        {!auths.length ? <Empty what="settlement authorisations" /> : (
          <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-b">
              <tr>
                {/* The backend returns the PSP identity for every authorisation
                    row; this table used to drop it, so two PSPs looked alike. */}
                <th className="text-left py-1.5 pr-3">PSP</th>
                <th className="text-left py-1.5 pr-3">Status</th>
                <th className="text-left py-1.5 pr-3">Type</th>
                <th className="text-left py-1.5 pr-3">Currency</th>
                <th className="text-right py-1.5 pr-3">Count</th>
                <th className="text-right py-1.5 pr-3">Authorised</th>
                <th className="text-right py-1.5">Net to provider</th>
              </tr>
            </thead>
            <tbody>
              {auths.map((a, i) => (
                <tr key={i} className="border-b last:border-0" data-testid={`psp-auth-row-${i}`}>
                  <td className="py-1.5 pr-3" data-testid={`psp-auth-provider-${i}`}>
                    {a.psp_provider || 'unknown'}
                  </td>
                  <td className="py-1.5 pr-3">{a.status}</td>
                  <td className="py-1.5 pr-3">{a.authorization_type}</td>
                  <td className="py-1.5 pr-3">{a.currency}</td>
                  <td className="py-1.5 pr-3 text-right">{a.count}</td>
                  <td className="py-1.5 pr-3 text-right">{money(a.amount, a.currency)}</td>
                  <td className="py-1.5 text-right">{money(a.net_to_clinic, a.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>
      {recon.length > 0 && (
        <section className="text-xs" data-testid="psp-reconciliation">
          <h4 className="text-sm font-semibold mb-2">Collection reconciliation</h4>
          {recon.map((r, i) => (
            <div key={i}>
              {r.provider}: {r.reconciled}/{r.records} collections matched · {r.unresolved_discrepancies} unresolved discrepancies
            </div>
          ))}
          {/* ACCOUNTING-TRUTH-01 (C1) — "1/1 reconciled" compares the COLLECTION
              only. It says nothing about distribution, release eligibility or
              bank credit, and an EUR collection is never compared numerically
              with a TRY distribution. */}
          <p className="text-[11px] text-muted-foreground mt-1" data-testid="psp-reconciliation-scope">
            Scope: collected amount versus PSP-reported collection only. Distribution,
            release eligibility, transfer and bank credit are separate facts and are
            not reconciled here.
          </p>
        </section>
      )}
      {/* ACCOUNTING-TRUTH-01 (C2) — this blocker belongs to the SUBSCRIPTION
          interface. Shown unconditionally, it told the reader "PSP fees: not
          available" on a page that was simultaneously displaying real PSP
          charges from a patient collection. Unknown is not zero, and a
          subscription limitation is not a statement about collections. */}
      {data.fees?.blocker && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-[11px] text-amber-900"
          data-testid="psp-fees-unavailable">
          Subscription PSP fees: not available (blocker {data.fees.blocker}). {data.fees?.note}
          {' '}This limitation applies to subscription billing only; patient-collection PSP
          charges are shown from their own evidence, in their own currency, where disclosed.
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">{data.classification_note}</p>
      {/* ACCOUNTING-TRUTH-01 (C4) — the collection→posting trace, stated apart
          from the PSP's distribution disclosure. A EUR posting is not wrong
          because the PSP settles in TRY. */}
      {data.accounting_trace && (
        <div className="bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-[11px] text-slate-800"
          data-testid="psp-accounting-trace">
          <span className="font-medium">Posting basis: </span>
          {data.accounting_trace.note}
          <span className="block mt-1 font-mono text-[10px]">
            {(data.accounting_trace.posting_path || []).join(' → ')}
          </span>
          <span className="block mt-1" data-testid="psp-settlement-posting-event">
            {data.accounting_trace.settlement_posting_event_exists
              ? 'A separate settlement posting event exists.'
              : 'No separate settlement posting event exists.'}{' '}
            {data.accounting_trace.settlement_evidence_proves}
          </span>
        </div>
      )}
    </div>
  );
}

function SubscriptionFinancials({ data }) {
  const groups = data.by_currency || [];
  const register = data.invoice_register || [];
  return (
    <div className="space-y-5" data-testid="subscription-financials-report">
      {!groups.length ? <Empty what="subscription invoices" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-b">
              <tr>
                <th className="text-left py-1.5 pr-3">Currency</th>
                <th className="text-right py-1.5 pr-3">Invoices</th>
                <th className="text-right py-1.5 pr-3">Renewals</th>
                <th className="text-right py-1.5 pr-3">Net</th>
                <th className="text-right py-1.5 pr-3">VAT</th>
                <th className="text-right py-1.5 pr-3">Gross</th>
                <th className="text-right py-1.5 pr-3">Collected</th>
                <th className="text-right py-1.5 pr-3">Open</th>
                <th className="text-right py-1.5 pr-3">Deferred</th>
                <th className="text-right py-1.5">Recognised</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <tr key={g.currency} className="border-b last:border-0"
                  data-testid={`subscription-financials-${g.currency}`}>
                  <td className="py-1.5 pr-3"><Badge variant="outline" className="text-[10px]">{g.currency}</Badge></td>
                  <td className="py-1.5 pr-3 text-right">{g.invoice_count}</td>
                  <td className="py-1.5 pr-3 text-right">{g.renewal_invoices}</td>
                  <td className="py-1.5 pr-3 text-right">{money(g.invoiced_net, g.currency)}</td>
                  <td className="py-1.5 pr-3 text-right">{money(g.invoiced_vat, g.currency)}</td>
                  <td className="py-1.5 pr-3 text-right font-medium">{money(g.invoiced_gross, g.currency)}</td>
                  <td className="py-1.5 pr-3 text-right">{money(g.collected, g.currency)}</td>
                  <td className="py-1.5 pr-3 text-right">{money(g.open_receivable, g.currency)}</td>
                  <td className="py-1.5 pr-3 text-right">{money(g.deferred_remaining, g.currency)}</td>
                  <td className="py-1.5 text-right">{money(g.revenue_recognised, g.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {register.length > 0 && (
        <section>
          <h4 className="text-sm font-semibold mb-2">Invoice register</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="subscription-invoice-register">
              <thead className="text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-1.5 pr-3">Invoice</th>
                  <th className="text-left py-1.5 pr-3">Type</th>
                  <th className="text-left py-1.5 pr-3">Period</th>
                  <th className="text-left py-1.5 pr-3">Due</th>
                  <th className="text-right py-1.5 pr-3">Net</th>
                  <th className="text-right py-1.5 pr-3">VAT</th>
                  <th className="text-right py-1.5 pr-3">Gross</th>
                  <th className="text-right py-1.5 pr-3">Paid</th>
                  <th className="text-left py-1.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {register.map(inv => (
                  <tr key={inv.invoice_number} className="border-b last:border-0">
                    <td className="py-1.5 pr-3 font-mono">{inv.invoice_number}</td>
                    <td className="py-1.5 pr-3">{inv.invoice_type}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{inv.period_start} → {inv.period_end}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{inv.due_date || '—'}</td>
                    <td className="py-1.5 pr-3 text-right">{money(inv.subtotal_net, inv.currency)}</td>
                    <td className="py-1.5 pr-3 text-right">{money(inv.tax_amount, inv.currency)}</td>
                    <td className="py-1.5 pr-3 text-right font-medium">{money(inv.total_gross, inv.currency)}</td>
                    <td className="py-1.5 pr-3 text-right">{money(inv.paid_amount, inv.currency)}</td>
                    <td className="py-1.5">{inv.is_refunded ? 'refunded' : inv.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Amounts state an explicit basis: Net excludes VAT, Gross = Net + VAT = amount collected.
          </p>
        </section>
      )}
      <CurrencyNote />
    </div>
  );
}

const RENDERERS = {
  'trial-balance': TrialBalance,
  'general-ledger': GeneralLedger,
  'ar-ap': ArAp,
  'deferred-revenue': DeferredRevenue,
  'vat-summary': VatSummary,
  'psp-settlement': PspSettlement,
  'subscription-financials': SubscriptionFinancials,
};

// ACCOUNTING-TRUTH-01 (B4) — which reports actually honour a date range. The
// dashboard's visible date filters were never sent, so changing them changed
// nothing; sending them to a report that ignores them would be just as
// dishonest, so the unsupported ones say so instead.
const DATE_AWARE_REPORTS = new Set(['trial-balance', 'general-ledger']);

export const AccountingGradeReports = ({ scopeLabel, fromDate, toDate, scopeKey }) => {
  const { t } = useTranslation();
  const [report, setReport] = useState('trial-balance');
  const [environment, setEnvironment] = useState('operational');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadTokenRef = React.useRef(0);

  const load = useCallback(async (key, env, from, to) => {
    const token = loadTokenRef.current + 1;
    loadTokenRef.current = token;
    setLoading(true);
    setError(null);
    try {
      const params = { environment: env };
      if (DATE_AWARE_REPORTS.has(key)) {
        if (from) params.from_date = from;
        if (to) params.to_date = to;
      }
      const res = await api.get(`/accounting/${key}`, { params });
      // A response for a superseded report / environment / workspace must never
      // be rendered: after a workspace switch it would show clinic A's figures
      // under clinic B.
      if (loadTokenRef.current !== token) return;
      setData(res.data);
    } catch (e) {
      if (loadTokenRef.current !== token) return;
      setError(
        e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || t('accountingReports.unavailable'),
      );
      setData(null);
    } finally {
      if (loadTokenRef.current === token) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Any canonical scope change invalidates what is on screen first, so a
    // stale report cannot sit under a new workspace while the next load runs.
    setData(null);
    load(report, environment, fromDate, toDate);
  }, [report, environment, fromDate, toDate, scopeKey, load]);

  const Renderer = RENDERERS[report];

  return (
    <Card data-testid="accounting-grade-reports">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">
            {t('accountingReports.title')}
            {scopeLabel && (
              <Badge variant="outline" className="ml-2 text-[10px]" data-testid="reports-scope">
                {scopeLabel}
              </Badge>
            )}
          </CardTitle>
          <Button size="sm" variant="outline"
            onClick={() => load(report, environment, fromDate, toDate)}
            disabled={loading} data-testid="reports-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {t('accountingReports.refresh')}
          </Button>
        </div>
        <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5 text-[11px] text-emerald-900"
          data-testid="posted-basis-note">
          <span className="font-medium">{t('accountingBasis.posted.title')} </span>
          {t('accountingBasis.posted.body')}
        </div>
        <div className="flex gap-1 flex-wrap mt-2" data-testid="reports-tab-bar">          {REPORTS.map(r => (
            <button
              key={r.key}
              onClick={() => setReport(r.key)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${report === r.key ? 'bg-slate-900 text-white' : 'bg-muted text-muted-foreground hover:text-gray-900'}`}
              data-testid={`report-tab-${r.key}`}
            >
              {r.label ? t(`accountingReports.reports.${r.key}`, { defaultValue: r.label }) : r.key}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2" data-testid="reports-date-scope-note">
          {DATE_AWARE_REPORTS.has(report)
            ? (fromDate || toDate
              ? `Date filter applied: ${fromDate || '…'} → ${toDate || '…'} (inclusive).`
              : 'No date filter applied — all posted periods.')
            : 'This report is a position as at today and does not use the date filter above.'}
        </p>
        <div className="flex items-center gap-1 flex-wrap mt-2" data-testid="reports-environment-bar">
          <span className="text-[11px] text-muted-foreground mr-1">
            {t('accountingReports.environmentLabel')}
          </span>          {ENVIRONMENTS.map(env => (
            <button
              key={env}
              onClick={() => setEnvironment(env)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${environment === env ? (env === 'sandbox' ? 'bg-amber-600 text-white' : 'bg-slate-700 text-white') : 'bg-muted text-muted-foreground hover:text-gray-900'}`}
              data-testid={`report-environment-${env}`}
            >
              {t(`accountingReports.environment.${env}`)}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <p className="text-xs text-muted-foreground py-4" data-testid="reports-loading">
            {t('accountingReports.loading')}
          </p>
        )}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-800"
            data-testid="reports-error">
            {String(error)}
          </div>
        )}
        {!loading && !error && data && (environment === 'sandbox' || environment === 'all') && (
          <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
            data-testid="reports-sandbox-warning">
            <AlertTriangle className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
            {t('accountingReports.sandboxWarning')}
          </div>
        )}
        {!loading && !error && data?.environment_breakdown?.unclassified_entries > 0 && (
          <div className="mb-3 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-700"
            data-testid="reports-unclassified-note">
            {t('accountingReports.unclassifiedNote', {
              count: data.environment_breakdown.unclassified_entries,
            })}
          </div>
        )}
        {!loading && !error && data && Renderer && <Renderer data={data} />}
      </CardContent>
    </Card>
  );
};

export default AccountingGradeReports;
